import { useState, useCallback, useContext, useRef } from "react";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { useBillImageWebview } from "./UseBillImageWebview";
import {
    billUploadHalfCardImpressionEvent,
    billUploadHalfCardProceedClickEvent,
    billUploadHalfCardDenyClickEvent,
    billUploadHalfCardCloseClickEvent,
} from "../analytics/events/BillImageHalfCard";

// No-op function for default callbacks
// eslint-disable-next-line @typescript-eslint/no-empty-function
const EmptyFunction = () => {};

export interface OpenBillImageHalfCardParams {
    context?: string;
    onBillImagesUpdated?: VoidFunction;
}

type AnalyticsEvent = (context: string | undefined) => void;

interface UseBillImageHalfCardReturn {
    show: boolean;
    open: (params: OpenBillImageHalfCardParams) => void;
    close: VoidFunction;
    onProceed: VoidFunction;
    onClose: VoidFunction;
    onDeny: VoidFunction;
    analyticsEvents: {
        billUploadHalfCardImpressionEvent: AnalyticsEvent;
        billUploadHalfCardProceedClickEvent: AnalyticsEvent;
        billUploadHalfCardDenyClickEvent: VoidFunction;
        billUploadHalfCardCloseClickEvent: VoidFunction;
    };
}

export const useBillImageHalfCard = (): UseBillImageHalfCardReturn => {
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    const { openBillImageWebview } = useBillImageWebview();

    // State for UI visibility
    const [show, setShow] = useState(false);
    const [eventContext, setEventContext] = useState<string | undefined>(undefined);

    // Refs for callbacks to avoid unnecessary re-renders
    const onBillImagesUpdatedRef = useRef<VoidFunction>(EmptyFunction);

    // Analytics event wrappers
    const handleImpressionEvent = useCallback(
        (context?: string) => {
            billUploadHalfCardImpressionEvent(impressionEvent, context);
        },
        [impressionEvent],
    );

    const handleProceedClickEvent = useCallback(
        (context?: string) => {
            billUploadHalfCardProceedClickEvent(clickEvent, context);
        },
        [clickEvent],
    );

    const handleDenyClickEvent = useCallback(() => {
        billUploadHalfCardDenyClickEvent(clickEvent);
    }, [clickEvent]);

    const handleCloseClickEvent = useCallback(() => {
        billUploadHalfCardCloseClickEvent(clickEvent);
    }, [clickEvent]);

    const open = useCallback(
        ({ onBillImagesUpdated, context }: OpenBillImageHalfCardParams) => {
            // Store callbacks in refs
            onBillImagesUpdatedRef.current = onBillImagesUpdated ?? EmptyFunction;

            setEventContext(context);
            setShow(true);
            handleImpressionEvent(context);
        },
        [handleImpressionEvent],
    );

    const close = useCallback(
        (isDeny?: boolean) => {
            setShow(false);
            isDeny ? handleDenyClickEvent() : handleCloseClickEvent();
        },
        [handleCloseClickEvent, handleDenyClickEvent],
    );

    const onDeny = useCallback(() => {
        setShow(false);
        handleDenyClickEvent();
    }, [handleDenyClickEvent]);

    const onProceed = useCallback(() => {
        setShow(false);
        handleProceedClickEvent(eventContext);

        openBillImageWebview(onBillImagesUpdatedRef.current);
    }, [handleProceedClickEvent, eventContext, openBillImageWebview, onBillImagesUpdatedRef]);

    const onClose = close;

    return {
        show,
        open,
        close,
        onProceed,
        onClose,
        onDeny,
        analyticsEvents: {
            billUploadHalfCardImpressionEvent: handleImpressionEvent,
            billUploadHalfCardProceedClickEvent: handleProceedClickEvent,
            billUploadHalfCardDenyClickEvent: handleDenyClickEvent,
            billUploadHalfCardCloseClickEvent: handleCloseClickEvent,
        },
    };
};
